<template>
  <SearchComponent v-model="query.search.searchTerm" />
  <div class="flex w-full flex-wrap justify-center gap-2 sm:justify-start">
    <TableDownloadButton v-model="query" :schema="schema" :endpoint-url="endpointUrl"></TableDownloadButton>
    <slot
      name="table-filter"
      :query="query"
      :schema="schema"
      :column-definitions="columnDefinitions"
      :update-query="
        (updatedQuery: WithRequiredProperty<GenericTableQuery<T>, 'filters' | 'pagination' | 'search' | 'sorting'>) =>
          (query = updatedQuery)
      "
    >
      <TableFilter v-model="query" :schema="schema" :column-definitions="columnDefinitions"> </TableFilter>
    </slot>
    <BaseTag :disabled="true" class="self-start">
      {{ $t("genericTable.totalItems", { total: totalItemsCount }) }}
    </BaseTag>
  </div>
  <div class="overflow-auto">
    <div v-if="entityList.length > 0">
      <div
        class="table w-full table-auto border-collapse rounded-t-lg bg-lightTertiaryColor dark:bg-inherit"
        :class="{ 'blur-[2px]': isLoading }"
      >
        <div class="hidden md:table-header-group">
          <template v-for="[fieldName, fieldDefinition] in visibleSchemaProperties" :key="fieldName">
            <TableHeadCell
              v-if="fieldDefinition"
              v-model:sorting="query.sorting"
              :text="fieldDefinition.columnName"
              :should-fill-column="fieldDefinition.shouldFillColumn"
              :field-name="String(fieldName)"
              :is-sortable="fieldDefinition.isSortable"
            >
              <template v-if="$slots[`header_${String(fieldName)}`]" #content>
                <slot :name="`header_${String(fieldName)}`" :entities="entityList"></slot>
              </template>
              <template v-if="!fieldDefinition.isCustomColumn">
                <FilterButton
                  v-if="fieldDefinition.isFilterable"
                  v-model:filters="query.filters[fieldName]"
                  :format="schema.fields[fieldName].format"
                >
                </FilterButton>
                <SortingIcon v-if="fieldDefinition.isSortable" v-model="query.sorting" :field="String(fieldName)"> </SortingIcon>
              </template>
            </TableHeadCell>
          </template>
        </div>
        <div class="table-row-group border">
          <TableRow v-for="entity in entityList" :key="getPrimaryKeyForEntity(entity)" :entity="entity" :row-config="rowConfig">
            <template v-for="[field, fieldDefinition] in visibleSchemaProperties" :key="field">
              <TableBodyCell
                v-if="fieldDefinition"
                :text="entity[field as keyof typeof entity]"
                :should-fill-column="fieldDefinition?.shouldFillColumn ?? false"
              >
                <span class="mr-xs font-bold md:hidden"> {{ fieldDefinition.columnName }}: </span>
                <slot :name="field" :entity="entity">
                  <template v-if="!fieldDefinition.isCustomColumn">
                    {{ getFieldValue({ value: entity[field as keyof typeof entity], format: schema.fields[field].format }) }}
                  </template>
                </slot>
              </TableBodyCell>
            </template>
          </TableRow>
        </div>
      </div>
    </div>
    <div v-else :class="{ 'blur-[2px]': isLoading }">
      {{ t("genericTable.noEntriesFound") }}
    </div>
  </div>
  <div v-if="query.pagination" class="flex flex-wrap justify-center gap-1">
    <PaginatonControls v-model:page="query.pagination.currentPage" v-model:number-of-pages="numberOfPages"> </PaginatonControls>
    <SelectPageSize v-model="query.pagination.pageSize" class="h-lg" :schema="schema"></SelectPageSize>
  </div>
</template>

<script setup lang="ts" generic="T extends GenericTableSchema, S extends GenericTableColumnDefinitions<T>">
import dayjs from "dayjs";
import { computed, onMounted, reactive, Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import BaseTag from "../../../components/BaseTag.vue";
import SearchComponent from "../../../components/SearchComponent.vue";
import { LanguageIsoCode } from "../../../composition-functions/language/LanguageIsoCode";
import router from "../../../router";
import { isLanguagesRecord } from "../../language/model.languagesRecord";
import { readFromLanguagesRecord } from "../../language/service.language";
import { createSequencialFunctionCallsHandler } from "../../utils/createSequencialFunctionCallsHandler";
import { debounceAsync } from "../../utils/debounceAsync";
import { GenericTableSchemaFormat } from "../enum.genericTableSchemaFormat";
import { fetchEntities } from "../service.genericTable";
import { generateUrlSearchParameters, initializeGenericTableQuery } from "../service.genericTableQuery";
import { EntityFromGenericTableColumnDefinition, GenericTableColumnDefinitions } from "../type.genericTableColumnDefinition";
import { GenericTableQuery } from "../type.genericTableQuery";
import { GenericTableRowConfig } from "../type.genericTableRowConfig";
import { GenericTableSchema } from "../type.genericTableSchema";
import FilterButton from "./table/FilterButton.vue";
import PaginatonControls from "./table/PaginationControls.vue";
import SelectPageSize from "./table/SelectPageSize.vue";
import SortingIcon from "./table/SortingIcon.vue";
import TableBodyCell from "./table/TableCell.vue";
import TableDownloadButton from "./table/TableDownloadButton.vue";
import TableFilter from "./table/TableFilter.vue";
import TableHeadCell from "./table/TableHeadCell.vue";
import TableRow from "./table/TableRow.vue";

const debounceLoadEntityList = debounceAsync({
  functionToCall: createSequencialFunctionCallsHandler({ functionToCall: loadEntityList, shouldReplaceLastCall: true }),
});
const { t, locale: language } = useI18n();
const props = withDefaults(
  defineProps<{
    rowConfig: GenericTableRowConfig<T, S>;
    endpointUrl: string;
    schema: T;
    columnDefinitions: S;
    /** When false, table state (filters, pagination, sort) is not synced to the route query. Use in modals/dialogs. */
    shouldSyncStateToUrl?: undefined | boolean;
  }>(),
  { shouldSyncStateToUrl: true },
);
const visibleSchemaProperties = computed(() =>
  Object.entries(props.columnDefinitions).filter(([, column]) => column && column.isVisible === true),
);
const query: WithRequiredProperty<GenericTableQuery<T>, "filters" | "pagination" | "search" | "sorting"> = reactive({
  filters: {},
  sorting: [],
  pagination: { currentPage: 1, pageSize: 20 },
  search: { searchTerm: "", searchColumns: [] },
});
const totalItemsCount = ref<number>(0);
const numberOfPages = ref<number>(0);
const entityList = ref<EntityFromGenericTableColumnDefinition<T, S>[]>([]) as Ref<EntityFromGenericTableColumnDefinition<T, S>[]>;
const isLoading = ref(false);
const primaryKeyFields = getPrimaryKeyFields();

type GetFieldValueParams = {
  value: unknown;
  format: string;
};
function getFieldValue({ value, format }: GetFieldValueParams) {
  if (format === GenericTableSchemaFormat.LANGUAGE_RECORD && isLanguagesRecord(value)) {
    return readFromLanguagesRecord({ record: value, languages: [language.value as LanguageIsoCode] });
  } else if (format === GenericTableSchemaFormat.DATE && typeof value === "string") {
    return dayjs(value).locale(language.value).format("L");
  } else if (format === GenericTableSchemaFormat.DATE_TIME && typeof value === "string") {
    return dayjs(value).locale(language.value).format("L LT");
  } else if (format === GenericTableSchemaFormat.BOOLEAN && typeof value === "boolean") {
    return value ? t("message.yes") : t("message.no");
  } else {
    return value;
  }
}

const refresh = loadEntityList;
defineExpose({
  refresh,
  entityList,
});
const isInitialized = ref(false);

watch(
  () => [query.filters, query.search.searchTerm],
  async () => {
    if (isInitialized.value) {
      if (query.pagination && query.pagination.currentPage !== 1) {
        query.pagination.currentPage = 1;
      } else {
        await debounceLoadEntityList();
      }
    }
  },
  { deep: true },
);

watch(
  () => [query.sorting, query.pagination?.pageSize],
  async () => {
    if (isInitialized.value && query.pagination) {
      if (query.pagination.currentPage !== 1) {
        query.pagination.currentPage = 1;
      } else {
        await loadEntityList();
      }
    }
  },
  { deep: true },
);

watch(
  () => [query.pagination?.currentPage],
  async () => {
    if (isInitialized.value) {
      await loadEntityList();
    }
  },
  { deep: true },
);

onMounted(async () => {
  const defaultValues = props.shouldSyncStateToUrl ? new URL(window.location.toString()).searchParams : new URLSearchParams();
  Object.assign(
    query,
    initializeGenericTableQuery({
      genericTableSchema: props.schema,
      defaultValues,
      genericTableColumnsDefinition: props.columnDefinitions,
    }),
  );

  await loadEntityList();
  isInitialized.value = true;
});

function getPrimaryKeyFields(): string[] {
  const keys = Object.entries(props.schema.fields)
    .filter(([_, fieldDefinition]) => "isPrimaryKey" in fieldDefinition && fieldDefinition.isPrimaryKey)
    .map(([key]) => key);
  if (!keys.length) throw new Error("Missing primary key column");
  return keys;
}

function getPrimaryKeyForEntity(entity: EntityFromGenericTableColumnDefinition<T, S>): string {
  const keyValues = [];
  for (const field of primaryKeyFields) {
    keyValues.push(entity[field as keyof EntityFromGenericTableColumnDefinition<T, S>]);
  }
  return keyValues.join("$-$");
}

async function loadEntityList() {
  const params: URLSearchParams = generateUrlSearchParameters<T>({
    ...query,
    select: undefined,
  });
  appendParamsToQuery(params);
  isLoading.value = true;
  const data = await fetchEntities<T, GenericTableQuery<T>>({
    schema: props.schema,
    path: props.endpointUrl,
    query: query as GenericTableQuery<T>,
  });
  totalItemsCount.value = data.totalItems;
  entityList.value = data.result as EntityFromGenericTableColumnDefinition<T, S>[];
  numberOfPages.value = Math.ceil(totalItemsCount.value / (query.pagination?.pageSize || 1));
  isLoading.value = false;
}

function appendParamsToQuery(params: URLSearchParams) {
  if (!props.shouldSyncStateToUrl) {
    return;
  }
  const queryObject: Record<string, string | string[]> = {};
  params.forEach((value, key) => {
    queryObject[key] = queryObject[key] ? ([] as string[]).concat(queryObject[key], value) : value;
  });
  router.replace({ query: queryObject });
}
</script>
